import React from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import BandwidthActivityByMonthChart from './BandwidthActivityByMonthChart';
import { SectionLabel } from '../kpi-dashboard/KpiDashboardContent';
import BandwidthActivityByWeekdayChart from './BandwidthActivityByWeekdayChart';
import BandwidthActivityTypeCountTimePanel from './BandwidthActivityTypeCountTimePanel';
import type { BandwidthDashboardData } from './bandwidthDashboardTypes';

export interface BandwidthDatesGridProps {
  dashboardQuery: UseQueryResult<BandwidthDashboardData, Error>;
  datesDrillMonth: string | null;
  datesDrillWeekday: number | null;
  onToggleDrillMonth: (monthKeyYyyyMm: string) => void;
  onToggleDrillWeekday: (monFirstIndex: number) => void;
  exportDateFrom: string;
  exportDateTo: string;
}

const BandwidthDatesGrid: React.FC<BandwidthDatesGridProps> = ({
  dashboardQuery,
  datesDrillMonth,
  datesDrillWeekday,
  onToggleDrillMonth,
  onToggleDrillWeekday,
  exportDateFrom,
  exportDateTo,
}) => {
  const { data, isLoading, isFetching, isError, error, refetch } = dashboardQuery;
  const baseError = isError ? error : null;

  if (isLoading && !data) {
    return (
      <div className="flex min-h-[420px] items-center justify-center rounded-2xl border border-indigo-100/80 bg-white/90 p-12 text-sm text-slate-500 shadow-sm shadow-indigo-900/5 ring-1 ring-slate-950/[0.03]">
        Loading dates analytics…
      </div>
    );
  }

  if (baseError) {
    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center gap-3 rounded-2xl border border-red-200/90 bg-red-50/50 p-12 text-center shadow-sm">
        <p className="text-sm text-red-800">{baseError instanceof Error ? baseError.message : 'Could not load data.'}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-red-800 shadow ring-1 ring-red-200 transition hover:bg-red-50"
        >
          Retry
        </button>
      </div>
    );
  }

  const d = data!;
  const busyHint = isFetching ? 'Updating…' : undefined;

  return (
    <div className="flex flex-col">
      {busyHint ? (
        <p className="mb-4 text-xs font-medium text-slate-500" aria-live="polite">
          {busyHint}
        </p>
      ) : null}
      <section className="mb-6 w-full" aria-labelledby="dates-activity-kpis-heading">
        <SectionLabel id="dates-activity-kpis-heading">Dates activity KPIs</SectionLabel>
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
          <BandwidthActivityByMonthChart
            title="Activity by Month"
            monthlyTrend={d.monthlyTrend}
            selectedMonthKey={datesDrillMonth}
            onMonthBarClick={onToggleDrillMonth}
          />
          <BandwidthActivityByWeekdayChart
            title="Activity by Day of Week"
            points={d.activityByDayOfWeek}
            selectedWeekdayIndex={datesDrillWeekday}
            onWeekdayBarClick={onToggleDrillWeekday}
          />
        </div>
      </section>
      <section className="mb-6 w-full">
        <BandwidthActivityTypeCountTimePanel
          title="Activity count and time by type"
          rows={d.activityTypeCountVsTime}
          exportDateFrom={exportDateFrom}
          exportDateTo={exportDateTo}
        />
      </section>
    </div>
  );
};

export default BandwidthDatesGrid;
